<route lang="yaml">
meta:
  layout: blank
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
  requiresAdmin: true
</route>

<script setup>
// RM-435 — RM Team Feed: what RM Team did, newest first. One line per action
// (the summary), the record it touched and when under it; failures stand out;
// the read-back and the receipt open on tap. Intent: "what did RM Team do,
// and did anything fail?" answered in one scan on a phone.
import { computed, onMounted, ref, watch } from 'vue'
import axios from '@axios'
import moment from 'moment'

const actions = ref([])
const counts = ref({ total: 0, failed: 0 })
const pending = ref(null)
const loading = ref(false)
const loadingMore = ref(false)
const hasMore = ref(false)
const error = ref('')
const filter = ref('all') // all | failed
const kind = ref('') // '' | restaurant | request | member | message
const search = ref('')
const expanded = ref(new Set())
let searchTimer = null

const KINDS = [
  { value: '', title: 'Everything' },
  { value: 'restaurant', title: 'Restaurants' },
  { value: 'request', title: 'Requests' },
  { value: 'member', title: 'Members' },
  { value: 'message', title: 'Messages' },
]

function params(before) {
  const p = { limit: 50 }
  if (filter.value === 'failed') p.status = 'failed'
  if (kind.value) p.kind = kind.value
  if (search.value.trim()) p.q = search.value.trim()
  if (before) p.before = before
  
  return p
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const [feed, pend] = await Promise.all([
      axios.get('/agent/admin/rm-team/feed', { params: params() }),
      axios.get('/agent/admin/rm-team/pending'),
    ])
    actions.value = feed.data.actions
    counts.value = feed.data.counts
    hasMore.value = feed.data.hasMore
    pending.value = pend.data.pending
  } catch (e) {
    error.value = 'The feed could not be loaded.'
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!actions.value.length) return
  loadingMore.value = true
  try {
    const last = actions.value[actions.value.length - 1]
    const res = await axios.get('/agent/admin/rm-team/feed', { params: params(last.executedAt) })
    actions.value = [...actions.value, ...res.data.actions]
    hasMore.value = res.data.hasMore
  } catch (e) {
    error.value = 'More of the feed could not be loaded.'
  } finally {
    loadingMore.value = false
  }
}

function toggle(id) {
  const next = new Set(expanded.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expanded.value = next
}

const KIND_LABEL = { restaurant: 'Restaurant', request: 'Request', member: 'Member', message: 'Message' }
const CHANNEL_LABEL = { web: 'app', sms: 'text' }

function when(d) {
  const m = moment(d)
  
  return m.isSame(moment(), 'day') ? `Today ${m.format('h:mma')}` : m.format('ddd M/D h:mma')
}

/** The feed grouped by day, in order. */
const days = computed(() => {
  const groups = []
  actions.value.forEach(a => {
    const key = moment(a.executedAt).format('YYYY-MM-DD')
    let g = groups[groups.length - 1]
    if (!g || g.key !== key) {
      g = { key, label: moment(a.executedAt).calendar(null, { sameDay: '[Today]', lastDay: '[Yesterday]', lastWeek: 'dddd M/D', sameElse: 'dddd M/D' }), items: [] }
      groups.push(g)
    }
    g.items.push(a)
  })

  return groups
})

watch([filter, kind], load)
watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(load, 300)
})
onMounted(load)
</script>

<template>
  <div class="feed-page content-wrapper">
    <header class="feed-head">
      <h1 class="feed-title">
        RM Team Feed
      </h1>
      <p class="feed-sub">
        What RM Team has done on your yes, newest first.
        <span v-if="counts.total">{{ counts.total }} action{{ counts.total === 1 ? '' : 's' }}<template v-if="counts.failed">, {{ counts.failed }} failed</template>.</span>
      </p>
    </header>

    <section
      v-if="pending"
      class="pending"
    >
      <div class="pending-label">
        Waiting for your yes
      </div>
      <pre class="pending-text">{{ pending.readBack }}</pre>
      <div class="meta">
        Asked {{ when(pending.createdAt) }} · {{ CHANNEL_LABEL[pending.channel] || pending.channel }} · expires {{ moment(pending.expiresAt).format('h:mma') }}
      </div>
    </section>

    <div class="controls">
      <VTextField
        v-model="search"
        placeholder="Search by name or action"
        density="compact"
        variant="outlined"
        hide-details
        clearable
        class="control-search"
      />
      <VSelect
        v-model="kind"
        :items="KINDS"
        density="compact"
        variant="outlined"
        hide-details
        class="control-kind"
      />
      <VBtnToggle
        v-model="filter"
        density="compact"
        mandatory
        divided
        class="control-filter"
      >
        <VBtn value="all">
          All
        </VBtn>
        <VBtn value="failed">
          Failed
        </VBtn>
      </VBtnToggle>
    </div>

    <p
      v-if="error"
      class="state state-error"
    >
      {{ error }}
    </p>

    <div
      v-if="loading"
      class="skeleton"
    >
      <div
        v-for="n in 6"
        :key="n"
        class="skeleton-row"
      />
    </div>

    <p
      v-else-if="!actions.length"
      class="state"
    >
      <template v-if="filter === 'failed'">
        Nothing has failed.
      </template>
      <template v-else-if="search || kind">
        Nothing matches.
      </template>
      <template v-else>
        Nothing yet. Ask RM Team for a change, in the app or by text, and it appears here once you say yes.
      </template>
    </p>

    <template v-else>
      <section
        v-for="day in days"
        :key="day.key"
        class="day"
      >
        <h2 class="day-label">
          {{ day.label }}
        </h2>
        <ul class="list">
          <li
            v-for="a in day.items"
            :key="a.id"
            class="row"
            :class="{ 'row-failed': a.status === 'failed', 'row-open': expanded.has(a.id) }"
          >
            <button
              type="button"
              class="row-main"
              :aria-expanded="expanded.has(a.id)"
              @click="toggle(a.id)"
            >
              <span class="row-summary">
                <span
                  v-if="a.status === 'failed'"
                  class="badge"
                >Failed</span>
                {{ a.summary }}
              </span>
              <span class="meta">
                {{ when(a.executedAt) }}
                <template v-if="a.target && a.target.kind"> · {{ KIND_LABEL[a.target.kind] || a.target.kind }}</template>
                · {{ CHANNEL_LABEL[a.channel] || a.channel }}
              </span>
            </button>
            <div
              v-if="expanded.has(a.id)"
              class="row-detail"
            >
              <p
                v-if="a.error"
                class="detail-error"
              >
                {{ a.error }}
              </p>
              <div class="detail-block">
                <div class="detail-label">
                  Read back
                </div>
                <pre class="detail-text">{{ a.readBack }}</pre>
              </div>
              <div class="detail-block">
                <div class="detail-label">
                  Receipt
                </div>
                <pre class="detail-text">{{ a.receipt }}</pre>
              </div>
              <div class="meta">
                {{ a.adminEmail }}<template v-if="a.durationMs != null">
                  · {{ (a.durationMs / 1000).toFixed(1) }}s
                </template>
              </div>
            </div>
          </li>
        </ul>
      </section>

      <div
        v-if="hasMore"
        class="more"
      >
        <VBtn
          variant="text"
          :loading="loadingMore"
          @click="loadMore"
        >
          Older
        </VBtn>
      </div>
    </template>
  </div>
</template>

<style scoped>
.feed-page {
  max-width: 720px;
  margin: 0 auto;
  padding: 24px 16px 48px;
}

.feed-head {
  margin-bottom: 20px;
}

.feed-title {
  font-size: 24px;
  font-weight: 600;
  line-height: 1.2;
  margin: 0 0 4px;
}

.feed-sub {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  margin: 0;
}

.pending {
  background: rgba(var(--v-theme-primary), 0.06);
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 20px;
}

.pending-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 8px;
}

.pending-text,
.detail-text {
  font: inherit;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
}

.controls {
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
  margin-bottom: 20px;
}

@media (min-width: 600px) {
  .controls {
    grid-template-columns: 1fr 180px auto;
    align-items: center;
  }
}

.controls :deep(input) {
  font-size: 16px;
}

.state {
  font-size: 14px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  padding: 24px 0;
  text-align: center;
}

.state-error {
  color: rgb(var(--v-theme-error));
}

.skeleton-row {
  height: 44px;
  border-radius: 8px;
  background: rgba(var(--v-theme-on-surface), 0.06);
  margin-bottom: 8px;
}

.day {
  margin-bottom: 24px;
}

.day-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin: 0 0 8px;
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.row {
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.row:last-child {
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.row-main {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: 0;
  padding: 12px 4px;
  cursor: pointer;
  color: inherit;
  font: inherit;
}

.row-main:hover,
.row-main:focus-visible {
  background: rgba(var(--v-theme-on-surface), 0.03);
  outline: none;
}

.row-main:focus-visible {
  box-shadow: inset 0 0 0 2px rgb(var(--v-theme-primary));
  border-radius: 8px;
}

.row-summary {
  display: block;
  font-size: 16px;
  line-height: 1.4;
}

.row-failed .row-summary {
  color: rgb(var(--v-theme-error));
}

.badge {
  display: inline-block;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 1px 6px;
  border-radius: 4px;
  background: rgba(var(--v-theme-error), 0.12);
  color: rgb(var(--v-theme-error));
  margin-right: 6px;
  vertical-align: middle;
}

.meta {
  display: block;
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), 0.55);
  margin-top: 4px;
}

.row-detail {
  padding: 0 4px 16px;
}

.detail-error {
  font-size: 14px;
  color: rgb(var(--v-theme-error));
  margin: 0 0 12px;
}

.detail-block {
  margin-bottom: 12px;
}

.detail-label {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(var(--v-theme-on-surface), 0.5);
  margin-bottom: 4px;
}

.more {
  text-align: center;
  padding: 8px 0;
}
</style>
